import { useEffect } from 'react';
import { useApp } from './store/app';
import { useTheme } from './store/theme';
import { bootstrapSource } from './lib/sources';
import { ensureServiceWorker } from './lib/swBridge';
import { installLifecycleHooks, restoreSpeed } from './player/engine';
import { applyTheme, watchSystemTheme } from './lib/theme';
import { onRouteChange, currentRoute, type Route } from './lib/router';
import { Library } from './ui/Library';
import { Player } from './ui/Player';
import { Settings } from './ui/Settings';
import { MiniPlayer } from './ui/MiniPlayer';
import { ResumeDialog } from './ui/ResumeDialog';
import { NmIconButton } from './ui/nm';
import { CogIcon, MoonIcon, RefreshIcon, SunIcon } from './ui/icons';

export function App() {
   const route = useApp((s) => s.route);
   const setRoute = useApp((s) => s.setRoute);
   const syncFromRemote = useApp((s) => s.syncFromRemote);
   const themeChoice = useTheme((s) => s.choice);
   const refreshTheme = useTheme((s) => s.refresh);

   useEffect(() => {
      applyTheme(themeChoice);
   }, [themeChoice]);

   useEffect(() => watchSystemTheme(refreshTheme), [refreshTheme]);

   useEffect(() => {
      setRoute(currentRoute());
      return onRouteChange(setRoute);
   }, [setRoute]);

   useEffect(() => installLifecycleHooks(), []);

   useEffect(() => {
      restoreSpeed();
      // Registered up front, not just when Drive connects: a fetch-handling
      // worker is what makes the app installable as a PWA, and having it already
      // in control avoids the "reload once more" step on first Drive use.
      void ensureServiceWorker();
      // Opens your Drive folder when it is set up, and the demo library otherwise.
      void bootstrapSource();
   }, []);

   // Coming back to the tab is the natural moment to notice that another device
   // moved ahead.
   useEffect(() => {
      const onFocus = () => void syncFromRemote();
      window.addEventListener('focus', onFocus);
      return () => window.removeEventListener('focus', onFocus);
   }, [syncFromRemote]);

   return (
      <div className="shell">
        {/* Keyed on the route so React remounts it and the entrance animation
         * replays — that is what turns navigation into a transition. */}
        <div className="shell__inner route" key={routeKey(route)}>
          {route.name === 'library' && (
             <>
                <TopBar />
                <Library />
             </>
          )}
          {route.name === 'player' && <Player bookId={route.bookId} />}
          {route.name === 'settings' && <Settings />}
        </div>
        <MiniPlayer />
        <ResumeDialog />
      </div>
   );
}

/** Distinct per screen, but stable while you stay on it — so switching books
  * replays the transition and a re-render inside one book does not. */
function routeKey(route: Route): string {
  return route.name === 'player' ? `player:${route.bookId}` : route.name;
}

function TopBar() {
  const navigate = useApp((s) => s.navigate);
  const loadLibrary = useApp((s) => s.loadLibrary);
  const label = useApp((s) => s.source?.label ?? '');
  const libraryStatus = useApp((s) => s.libraryStatus);
  const resolved = useTheme((s) => s.resolved);
  const setChoice = useTheme((s) => s.setChoice);

  return (
     <header className="topbar">
       <h1 className="topbar__title">
         Shelf
         {label && <span className="topbar__sub">{label}</span>}
       </h1>

       <NmIconButton
         label="Refresh library"
         onClick={() => void loadLibrary({ force: true })}
         disabled={libraryStatus === 'loading'}
       >
         <RefreshIcon size={19} />
       </NmIconButton>

       <NmIconButton
         label={resolved === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
         onClick={() => setChoice(resolved === 'dark' ? 'light' : 'dark')}
       >
         {resolved === 'dark' ?
           <SunIcon size={19} />
         : <MoonIcon size={19} />}
       </NmIconButton>

       <NmIconButton label="Settings" onClick={() => navigate({ name: 'settings' })}>
         <CogIcon size={19} />
       </NmIconButton>
     </header>
  );
}
